import React, { FunctionComponent, lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Deeplink } from './components/global/Deeplink';
import { Loading } from './components/global/Loading';
import { AppContext } from './contexts/AppContext';
import { TokenStore } from './data/stores/TokenStore';
import { useDecision } from '@optimizely/react-sdk';
import { Maintenance } from './pages/Maintenance';
import { observer } from 'mobx-react';
import { ROUTE_URLS } from './config/Routes';
import { GroupsController } from './controllers/GroupsController';

const LoginLayout = lazy(() => import('./components/layout/LoginLayout'));
const Signup = lazy(() => import('./pages/Signup'));
const Login = lazy(() => import('./pages/Login'));
const RegisterInterest = lazy(() => import('./pages/RegisterInterest'));
const WebsiteLayout = lazy(() => import('./components/layout/WebsiteLayout'));
const AcceptTerms = lazy(() => import('./pages/AcceptTerms'));
const AppLayout = lazy(() => import('./components/layout/AppLayout'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Sources = lazy(() => import('./pages/Sources'));
const Connect = lazy(() => import('./pages/Connect'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Rules = lazy(() => import('./pages/Rules'));
const Rule = lazy(() => import('./pages/Rule'));
const Apportionments = lazy(() => import('./pages/Apportionments'));
const Calculations = lazy(() => import('./pages/Calculations'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Invoices = lazy(() => import('./pages/Invoices'));
const RechargeTransactions = lazy(() => import('./pages/RechargeTransactions'));
const LoanAccounts = lazy(() => import('./pages/loan-account/LoanAccounts'));
const TransactionDetails = lazy(() => import('./pages/loan-account/TransactionDetails'));
const EditTransactionGroups = lazy(() => import('./pages/loan-account/EditTransactionGroups'));
const BragSetup = lazy(() => import('./pages/brag/Setup'));
const ReconciliationLog = lazy(() => import('./pages/brag/ReconciliationLog'));
const Attachment = lazy(() => import('./pages/Attachment'));
const Settings = lazy(() => import('./pages/Settings'));
const Home = lazy(() => import('./pages/Home'));

export const Routes: FunctionComponent = observer(() => {
  const controller = useRef(new GroupsController());
  const { UserStore, GroupStore } = useContext(AppContext);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [maintenanceEnabled] = useDecision('show_downtime_page', { autoUpdate: true });

  useEffect(() => {
    if (TokenStore.instance().hasToken) {
      setGroupsLoaded(false);
      (async () => {
        await controller.current.init();
        setGroupsLoaded(true);
      })();
    }
  }, [TokenStore.instance().hasToken]);

  if (maintenanceEnabled?.enabled) {
    return <Maintenance />;
  }

  if (!TokenStore.instance().hasToken) {
    return (
      <Suspense fallback={<Loading />}>
        <LoginLayout>
          <Switch>
            <Route exact path="/signup">
              <Signup />
            </Route>
            <Route exact path="/login/complete">
              <Login complete />
            </Route>
            <Route exact path="/login/go">
              <Login initiate />
            </Route>
            <Route exact path="/login">
              <Login />
            </Route>
            <Route exact path="/register">
              <RegisterInterest />
            </Route>
            <Route>
              <Deeplink>
                <Login />
              </Deeplink>
            </Route>
          </Switch>
        </LoginLayout>
      </Suspense>
    );
  }

  if (UserStore.user && !UserStore.user.hasAcceptedTerms) {
    return (
      <Suspense fallback={<Loading />}>
        <WebsiteLayout>
          <AcceptTerms />
        </WebsiteLayout>
      </Suspense>
    );
  }

  if (!groupsLoaded) {
    return <Loading />;
  }

  const defaultGroup = GroupStore.defaultGroup();
  if (!defaultGroup) {
    // User doesn't have access to any groups, forward them to /onboarding regardless of what page they are on
    return (
      <Suspense fallback={<Loading />}>
        <AppLayout>
          <Switch>
            <Route exact path="/onboarding">
              <Onboarding create />
            </Route>
            <Redirect to="/onboarding"/>
          </Switch>
        </AppLayout>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading />}>
      <AppLayout>
        <Switch>
          {/* User has access to other groups but wants to start onboarding for a new group*/}
          <Route exact path="/onboarding">
            <Onboarding create />
          </Route>
          {/* Continue onboarding for current group */}
          <Route exact path="/groups/:groupId/onboarding">
            <Onboarding />
          </Route>
          <Route exact path="/groups/:groupId/sources">
            <Sources />
          </Route>
          <Route exact path="/groups/:groupId/sources/:entityId">
            <Sources />
          </Route>
          <Route exact path="/groups/connect">
            <Connect />
          </Route>
          <Route exact path="/groups/:groupId/notifications">
            <Notifications />
          </Route>

          {/* Recharger */}
          <Route exact path={ROUTE_URLS.rules}>
            <Rules />
          </Route>
          <Route exact path="/groups/:groupId/entities/:entityId">
            <Rules />
          </Route>
          <Route exact path="/groups/:groupId/entities/:entityId/:type">
            <Rules />
          </Route>
          <Route
            exact
            path="/groups/:groupId/entities/:entityId/rules/:type/new"
          >
            <Rule />
          </Route>
          <Route
            exact
            path="/groups/:groupId/entities/:entityId/rules/:ruleId/edit"
          >
            <Rule />
          </Route>
          <Route
            exact
            path="/groups/:groupId/entities/:entityId/rules/:ruleId/clone"
          >
            <Rule clone />
          </Route>
          <Route exact path="/groups/:groupId/apportionments">
            <Apportionments />
          </Route>
          <Route exact path="/groups/:groupId/apportionments/:entityId">
            <Apportionments />
          </Route>
          <Route exact path="/groups/:groupId/calculations">
            <Calculations />
          </Route>
          <Route
            exact
            path="/groups/:groupId/calculations/:calculationId/transactions"
          >
            <Transactions />
          </Route>
          <Route
            exact
            path="/groups/:groupId/calculations/:calculationId/invoices"
          >
            <Invoices />
          </Route>
          <Route
            exact
            path="/groups/:groupId/calculations/:calculationId/recharge-transactions"
          >
            <RechargeTransactions />
          </Route>

          {/* Balancer */}
          <Route exact path="/groups/:groupId/loans/dashboard">
            <LoanAccounts />
          </Route>
          <Route exact path="/groups/:groupId/loans/:loanId">
            <TransactionDetails />
          </Route>
          <Route exact path="/groups/:groupId/loans/:loanId/edit">
            <EditTransactionGroups />
          </Route>
          <Redirect
            from="/groups/:groupId/loans"
            to="/groups/:groupId/loans/dashboard"
          />

          {/* Brag */}
          <Route exact path="/groups/:groupId/brag/setup">
            <BragSetup />
          </Route>
          <Route exact path="/groups/:groupId/brag/reconciliation-log">
            <ReconciliationLog />
          </Route>
          <Redirect
            from="/groups/:groupId/brag"
            to="/groups/:groupId/brag/setup"
          />

          <Route path="/groups/:groupId/attachments/:attachmentId">
            <Attachment />
          </Route>
          <Route path="/groups/:groupId/settings">
            <Settings />
          </Route>
          <Route exact path="/logout">
            <Login logout />
          </Route>

          <Route exact path="/groups/:groupId">
            <Home />
          </Route>
          <Route exact path="/extension/connect">
            <Redirect to={`/groups/${defaultGroup.id}/brag/setup`} />
          </Route>
          <Route>
            <Redirect to={`/groups/${defaultGroup.id}`} />
          </Route>
        </Switch>
      </AppLayout>
    </Suspense>
  );
});
